import { unmaskNumber } from '@givebutter/util';
import {
    BranchFormValues,
    DateRangeValues,
    DelayFormValues,
    SetCustomFieldFormValues,
} from 'assets/js/pages/engage/workflows/actions/types';
import { anyCampaignItem } from 'assets/js/pages/engage/workflows/components/CampaignSelect';
import { anyWidgetItem } from 'assets/js/pages/engage/workflows/components/WidgetSelect';
import {
    AnniversaryYearOptions,
    DonationMadeTriggerFormValues,
    TimeAgoCustomUnit,
    TimeAgoIntervalValue,
} from 'assets/js/pages/engage/workflows/triggers/types';
import { FormData, TagsOperator } from 'assets/js/pages/engage/workflows/types';
import {
    baseNodeDefaults,
    utilityNodeTypes,
} from 'assets/js/pages/engage/workflows/utils/constants';
import { NodeWithData } from 'assets/js/pages/engage/workflows/utils/types';
import {
    WorkflowActionTypeEnum,
    WorkflowTriggerTypeEnum,
    WorkflowUtilityNodeTypeEnum,
} from 'constants/workflows';
import { filterUndefined } from 'helpers/arrays';
import { isoDateFromServer } from 'helpers/dates';
import {
    DelayWorkflowStep,
    FilterCustomField,
    SetCustomFieldWorkflowStep,
    Workflow,
    WorkflowFilter,
    WorkflowFilterComparisonOperator,
    WorkflowFilterFormComparisonOperator,
    WorkflowFilterFrequency,
    WorkflowStep,
} from 'types/domain';

interface TransformNodesToStepsOptions {
    nodes: NodeWithData[];
    workflowId: number;
}

interface TransformStepsToNodesOptions {
    steps: WorkflowStep[];
    workflow: Workflow;
    selectable?: boolean;
    customFieldOptions: FilterCustomField[];
}

interface TransformNodeToStepOptions {
    node: NodeWithData;
    workflowId: number;
}

interface TransformStepToNodeOptions extends TransformStepsToNodesOptions {
    step: WorkflowStep;
    currentIndex: number;
}

const transformNodesToSteps = ({ nodes, workflowId }: TransformNodesToStepsOptions) => {
    const steps: WorkflowStep[] = [];
    const stepNodes = nodes.filter(n => n.type && !utilityNodeTypes.includes(n.type));

    stepNodes.forEach(node => {
        const step = transformNodeToStep({ node, workflowId });
        if (step) steps.push(step);
    });

    return steps;
};

const transformNodeToStep = ({
    node,
    workflowId,
}: TransformNodeToStepOptions): WorkflowStep | undefined => {
    if (!node.data?.formData) return;

    switch (node.data.type) {
        // triggers
        case WorkflowTriggerTypeEnum.ABANDONED_DONATION_FORM:
        case WorkflowTriggerTypeEnum.ATTENDED_EVENT:
        case WorkflowTriggerTypeEnum.DID_NOT_WIN_AUCTION:
        case WorkflowTriggerTypeEnum.CONTACT_FIRST_DONATION:
        case WorkflowTriggerTypeEnum.TICKET_CREATED:
        case WorkflowTriggerTypeEnum.REGISTERED_FOR_AUCTION:
        case WorkflowTriggerTypeEnum.TEAM_MEMBER_REGISTERED: {
            const { formData, parentId } = node.data;
            const campaignFilter = getCampaignFilter(formData?.values.campaigns);

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: 'trigger',
                action_data: {},
                filters: filterUndefined([campaignFilter]),
            };
        }
        case WorkflowTriggerTypeEnum.PLAN_CREATED:
        case WorkflowTriggerTypeEnum.PLAN_CANCELED: {
            const { formData, parentId } = node.data;

            const campaignFilter = getCampaignFilter(formData?.values.campaigns);
            const frequencyFilter = getFrequencyFilter(formData?.values.frequency);
            const donationFilters = getAmountRangeFilters(
                'recurring_amount',
                formData?.values.recurringOperator,
                formData?.values.recurringAmount,
                formData?.values.recurringAmountEnd,
            );

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: 'trigger',
                action_data: {},
                filters: filterUndefined([campaignFilter, frequencyFilter, ...donationFilters]),
            };
        }
        case WorkflowTriggerTypeEnum.DONATION_CREATED: {
            const { formData, parentId } = node.data;

            const campaignFilter = getCampaignFilter(formData?.values.campaigns);
            const donationFilters = getAmountRangeFilters(
                'donation_made',
                formData?.values.donationOperator,
                formData?.values.donationAmount,
                formData?.values.donationAmountEnd,
            );

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: 'trigger',
                action_data: {},
                filters: filterUndefined([campaignFilter, ...donationFilters]),
            };
        }
        case WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION: {
            const { formData, parentId } = node.data;
            const campaignFilter = getCampaignFilter(formData?.values.campaigns);
            const timeAgoFilter = getTimeAgoFilter(formData?.values);

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: 'trigger',
                action_data: {},
                filters: filterUndefined([campaignFilter, timeAgoFilter]),
            };
        }
        case WorkflowTriggerTypeEnum.PLAN_ANNIVERSARY: {
            const { formData, parentId } = node.data;
            const frequencyFilter = getFrequencyFilter(formData?.values.frequency);
            const anniversaryFilter = getAnniversaryYearFilter(formData?.values.anniversary);
            const recurringAmountFilters = getAmountRangeFilters(
                'recurring_amount',
                formData?.values.recurringOperator,
                formData?.values.recurringAmount,
                formData?.values.recurringAmountEnd,
            );

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: 'trigger',
                action_data: {},
                filters: filterUndefined([
                    frequencyFilter,
                    anniversaryFilter,
                    ...recurringAmountFilters,
                ]),
            };
        }

        case WorkflowTriggerTypeEnum.CONTACT_BIRTHDAY:
        case WorkflowTriggerTypeEnum.CONTACT_TAGGED: {
            const { formData, parentId } = node.data;
            const tagsFilter = getTagsFilter(formData?.values);

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: 'trigger',
                action_data: {},
                filters: filterUndefined([tagsFilter]),
            };
        }

        case WorkflowTriggerTypeEnum.TEAM_MEMBER_MILESTONE: {
            const { formData, parentId } = node.data;
            const campaignFilter = getCampaignFilter(formData?.values.campaigns);
            const amountRaisedFilters = getAmountRangeFilters(
                'amount_raised',
                formData?.values.amountRaisedOperator,
                formData?.values.amountRaised,
                formData?.values.amountRaisedEnd,
            );
            const supportersFilters = getAmountRangeFilters(
                'supporters',
                formData?.values.totalSupportersOperator,
                formData?.values.totalSupporters,
                formData?.values.totalSupportersEnd,
            );

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: 'trigger',
                action_data: {},
                filters: filterUndefined([
                    campaignFilter,
                    ...amountRaisedFilters,
                    ...supportersFilters,
                ]),
            };
        }

        case WorkflowTriggerTypeEnum.CONTACT_TOTAL_CONTRIBUTIONS: {
            const { formData, parentId } = node.data;
            const campaignFilter = getCampaignFilter(formData?.values.campaigns);
            const contributionsFilters = getAmountRangeFilters(
                'total_contributions',
                formData?.values.contributionOperator,
                formData?.values.contributionAmount,
            );

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: 'trigger',
                action_data: {},
                filters: filterUndefined([campaignFilter, ...contributionsFilters]),
            };
        }

        case WorkflowTriggerTypeEnum.WIDGET_SUBSCRIBED: {
            const { formData, parentId } = node.data;
            const widgetFilter = getWidgetFilter(formData?.values.widgets);

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: 'trigger',
                action_data: {},
                filters: filterUndefined([widgetFilter]),
            };
        }

        // actions
        case WorkflowUtilityNodeTypeEnum.DELAY: {
            const { formData, parentId } = node.data;
            const actionData = getDelayNodeActionData(formData);

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: WorkflowUtilityNodeTypeEnum.DELAY,
                action_data: actionData,
                filters: [],
            };
        }

        case WorkflowActionTypeEnum.ADD_NOTE: {
            const { formData, parentId, type } = node.data;

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: type,
                action_data: {
                    note: formData.values.note,
                },
                filters: [],
            };
        }

        case WorkflowActionTypeEnum.SEND_EMAIL: {
            const {
                formData: { values },
                parentId,
                type,
            } = node.data;
            const senderId = parseIntOrUndefined(values.sender_id);
            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: type,
                action_data: {
                    ...values,
                    sender_id: senderId,
                    message_id: node.data.messageId,
                    channel: 'email',
                    message_template_id: values.message_template_id,
                },
                filters: [],
            };
        }
        case WorkflowActionTypeEnum.SEND_SMS: {
            const {
                formData: { values },
                parentId,
                type,
            } = node.data;
            const senderId = parseIntOrUndefined(values.sender_id);
            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: type,
                action_data: {
                    ...values,
                    sender_id: senderId,
                    message_id: node.data.messageId,
                    channel: 'sms',
                    message_template_id: values.message_template_id,
                },
                filters: [],
            };
        }
        case WorkflowActionTypeEnum.SET_CUSTOM_FIELD: {
            const { formData, parentId } = node.data;
            if (formData.values.fieldId === '') break;

            let answer: SetCustomFieldWorkflowStep['action_data']['answer'];
            switch (formData.values.fieldType) {
                case 'text':
                case 'radio':
                case 'date':
                case 'select':
                case 'textarea':
                    answer = formData.values.fieldValue as string;
                    break;
                case 'number':
                case 'percentage':
                    answer = formData.values.fieldValue as number;
                    break;
                case 'daterange': {
                    const { start, end } = formData.values.fieldValue as DateRangeValues;
                    answer = [start, end] as [string, string];
                    break;
                }
                case 'checkbox':
                    answer = formData.values.fieldValue as boolean;
                    break;
                case 'multiselect':
                    answer = formData.values.fieldValue as string[];
                    break;
                default:
                    answer = '';
            }

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: node.data.type,
                action_data: {
                    type: formData.values.fieldType,
                    custom_field_id: formData.values.fieldId,
                    answer: answer,
                } as SetCustomFieldWorkflowStep['action_data'],
                filters: [],
            };
        }
        case WorkflowActionTypeEnum.CONTACT_TAG: {
            const {
                formData: {
                    values: { action, tags },
                },
                parentId,
            } = node.data;

            if (action === 'tag') {
                return {
                    id: node.data.stepId,
                    uuid: node.id,
                    parent_uuid: parentId,
                    workflow_id: workflowId,
                    action: WorkflowActionTypeEnum.CONTACT_TAG,
                    action_data: {
                        type: 'tag',
                        names: tags,
                    },
                    filters: [],
                };
            }

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: WorkflowActionTypeEnum.CONTACT_UNTAG,
                action_data: {
                    type: 'untag',
                    names: tags,
                },
                filters: [],
            };
        }
        case WorkflowActionTypeEnum.TASK_CREATE: {
            const { formData, parentId, type } = node.data;

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: type,
                action_data: {
                    title: formData.values.title,
                    description: formData.values.description,
                    due_at: formData.values.due_at
                        ? {
                              ...formData.values.due_at,
                              value: unmaskNumber(formData.values.due_at.value),
                          }
                        : undefined,
                    assigned_to: parseInt(formData.values.assigned_to[0]),
                    status: formData.values.status,
                },
                filters: [],
            };
        }
        case WorkflowUtilityNodeTypeEnum.BRANCH: {
            const { formData, parentId, type } = node.data;
            const { values } = formData;

            const filters = getBranchFiltersFromValue(values);

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: type,
                action_data: {
                    condition: values.condition,
                },
                filters,
            };
        }
        case WorkflowUtilityNodeTypeEnum.BRANCH_CONDITION: {
            const { formData, parentId, type } = node.data;

            return {
                id: node.data.stepId,
                uuid: node.id,
                parent_uuid: parentId,
                workflow_id: workflowId,
                action: type,
                action_data: {
                    value: formData.values.value,
                },
                filters: [],
            };
        }
    }
};

const transformStepsToNodes = ({
    steps,
    workflow,
    selectable = true,
    customFieldOptions = [],
}: TransformStepsToNodesOptions) => {
    const nodes: NodeWithData[] = [];

    steps.forEach((step, index) => {
        const node = transformStepToNode({
            step,
            workflow,
            selectable,
            customFieldOptions,
            steps,
            currentIndex: index,
        });
        if (node) nodes.push(node);
    });

    return nodes;
};

const transformStepToNode = ({
    step,
    workflow,
    selectable,
    customFieldOptions,
}: TransformStepToNodeOptions): NodeWithData | undefined => {
    switch (step.action) {
        case 'trigger': {
            const triggerNode = transformTriggerStepToNode(step, workflow.trigger, selectable);
            if (triggerNode) return triggerNode;
            break;
        }
        case WorkflowUtilityNodeTypeEnum.DELAY: {
            const {
                action_data: { value, time, skip_weekends },
            } = step;

            let values: DelayFormValues;
            if (time) {
                values = {
                    preset: 'custom',
                    custom_value: value.split(' ').length === 1 ? '0' : value.split(' ')[0],
                    custom_unit: value.split(' ').length === 1 ? value : value.split(' ')[1],
                    custom_time: isoDateFromServer(time),
                    skip_weekends,
                };
            } else {
                values = {
                    preset: value,
                    skip_weekends,
                };
            }
            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type: WorkflowUtilityNodeTypeEnum.DELAY,
                selectable,
                data: {
                    type: WorkflowUtilityNodeTypeEnum.DELAY,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: values,
                    },
                },
            };
        }
        case WorkflowActionTypeEnum.SEND_EMAIL: {
            const { message_id, channel, ...formValues } = step.action_data;
            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type: WorkflowActionTypeEnum.SEND_EMAIL,
                selectable,
                data: {
                    type: WorkflowActionTypeEnum.SEND_EMAIL,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    messageId: message_id,
                    formData: {
                        ...formDefaults,
                        values: {
                            ...formValues,
                            sender_id: formValues.sender_id?.toString(),
                        },
                    },
                },
            };
        }
        case WorkflowActionTypeEnum.SEND_SMS: {
            const { message_id, channel, ...formValues } = step.action_data;
            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type: WorkflowActionTypeEnum.SEND_SMS,
                selectable,
                data: {
                    type: WorkflowActionTypeEnum.SEND_SMS,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    messageId: message_id,
                    formData: {
                        ...formDefaults,
                        values: {
                            ...formValues,
                            sender_id: formValues.sender_id?.toString(),
                        },
                    },
                },
            };
        }
        case WorkflowActionTypeEnum.CONTACT_TAG:
        case WorkflowActionTypeEnum.CONTACT_UNTAG: {
            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type: WorkflowActionTypeEnum.CONTACT_TAG,
                selectable,
                data: {
                    type: WorkflowActionTypeEnum.CONTACT_TAG,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: {
                            action:
                                step.action === WorkflowActionTypeEnum.CONTACT_TAG
                                    ? 'tag'
                                    : 'untag',
                            tags: step.action_data.names,
                        },
                    },
                },
            };
        }
        case WorkflowActionTypeEnum.ADD_NOTE: {
            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type: WorkflowActionTypeEnum.ADD_NOTE,
                selectable,
                data: {
                    type: WorkflowActionTypeEnum.ADD_NOTE,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: step.action_data,
                    },
                },
            };
        }
        case WorkflowActionTypeEnum.SET_CUSTOM_FIELD: {
            const field = customFieldOptions.find(
                o => o.id.toString() === step.action_data.custom_field_id.toString(),
            );
            if (!field) break;

            let values: SetCustomFieldFormValues = {
                fieldId: field.id.toString(),
                fieldType: field.type,
                fieldValue:
                    step.action_data.type === 'daterange'
                        ? {
                              start: step.action_data.answer[0],
                              end: step.action_data.answer[1],
                          }
                        : step.action_data.answer,
            };

            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type: WorkflowActionTypeEnum.SET_CUSTOM_FIELD,
                selectable,
                data: {
                    type: WorkflowActionTypeEnum.SET_CUSTOM_FIELD,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values,
                    },
                },
            };
        }
        case WorkflowActionTypeEnum.TASK_CREATE: {
            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type: WorkflowActionTypeEnum.TASK_CREATE,
                selectable,
                data: {
                    type: WorkflowActionTypeEnum.TASK_CREATE,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: {
                            ...step.action_data,
                            assigned_to: step.action_data.assigned_to
                                ? [step.action_data.assigned_to.toString()]
                                : [],
                        },
                    },
                },
            };
        }
        case WorkflowUtilityNodeTypeEnum.BRANCH: {
            let values: BranchFormValues = {
                condition: step.action_data.condition,
                value: null,
            };

            const tagFilter = step.filters.find(s => s.type === 'tag');
            const campaignFilter = step.filters.find(s => s.type === 'campaign');
            const messageFilter = step.filters.find(s => s.type === 'message');
            const includeAllTimeFilter = step.filters.find(s => s.type === 'include_all_time');

            if (tagFilter) {
                values.value = {
                    operator: tagFilter?.value as TagsOperator,
                    tags: tagFilter?.values as string[],
                };
            }

            if (campaignFilter) {
                values.value = {
                    campaigns: transformCampaignFilterToSelectItems(campaignFilter),
                };
                if (includeAllTimeFilter) {
                    values.include_all_time = !!includeAllTimeFilter?.value;
                }
            }

            if (messageFilter) {
                values.value = {
                    messages: transformMessageFilterToSelectItems(messageFilter),
                };
            }

            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type: WorkflowUtilityNodeTypeEnum.BRANCH,
                selectable,
                data: {
                    type: WorkflowUtilityNodeTypeEnum.BRANCH,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: values ?? null,
                    },
                },
            };
        }
        case WorkflowUtilityNodeTypeEnum.BRANCH_CONDITION: {
            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type: WorkflowUtilityNodeTypeEnum.BRANCH_CONDITION,
                selectable: false,
                data: {
                    type: WorkflowUtilityNodeTypeEnum.BRANCH_CONDITION,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: step.action_data,
                    },
                },
            };
        }
    }
};

const transformTriggerStepToNode = (
    step: WorkflowStep,
    type: Workflow['trigger'],
    selectable: boolean = false,
): NodeWithData | undefined => {
    switch (type) {
        case WorkflowTriggerTypeEnum.ABANDONED_DONATION_FORM:
        case WorkflowTriggerTypeEnum.TICKET_CREATED:
        case WorkflowTriggerTypeEnum.DID_NOT_WIN_AUCTION:
        case WorkflowTriggerTypeEnum.ATTENDED_EVENT:
        case WorkflowTriggerTypeEnum.CONTACT_FIRST_DONATION:
        case WorkflowTriggerTypeEnum.REGISTERED_FOR_AUCTION:
        case WorkflowTriggerTypeEnum.TEAM_MEMBER_REGISTERED: {
            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type,
                selectable,
                data: {
                    type,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: {
                            campaigns: transformCampaignFilterToSelectItems(
                                step.filters.find(s => s.type === 'campaign'),
                            ),
                        },
                    },
                },
            };
        }
        case WorkflowTriggerTypeEnum.PLAN_CANCELED:
        case WorkflowTriggerTypeEnum.PLAN_CREATED: {
            const campaignFilter = step.filters.find(s => s.type === 'campaign');
            const frequencyFilter = step.filters.find(s => s.type === 'frequency');
            const transactionFilters = step.filters.filter(
                s => s.type === 'value' && s.subtype === 'recurring_amount',
            );

            const [recurringOperator, recurringAmount, recurringAmountEnd] =
                transformRangeToParts(transactionFilters);

            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type,
                selectable,
                data: {
                    type,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: {
                            campaigns: transformCampaignFilterToSelectItems(campaignFilter),
                            frequency: frequencyFilter?.value
                                ? (frequencyFilter.value as WorkflowFilterFrequency)
                                : 'any',
                            recurringAmount,
                            recurringAmountEnd,
                            recurringOperator,
                        },
                    },
                },
            };
        }
        case WorkflowTriggerTypeEnum.DONATION_CREATED: {
            const campaignFilter = step.filters.find(s => s.type === 'campaign');
            const donationFilters = step.filters.filter(s => s.type === 'value');

            const [donationOperator, donationAmount, donationAmountEnd] =
                transformRangeToParts(donationFilters);

            const donationFormValues: DonationMadeTriggerFormValues = {
                campaigns: transformCampaignFilterToSelectItems(campaignFilter),
                donationAmount,
                donationAmountEnd,
                donationOperator,
            };

            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type,
                selectable,
                data: {
                    type,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: donationFormValues,
                    },
                },
            };
        }
        case WorkflowTriggerTypeEnum.CONTACT_LAST_DONATION: {
            const campaignFilter = step.filters.find(s => s.type === 'campaign');
            const timeAgoFilter = step.filters.find(s => s.type === 'time_ago');

            if (!timeAgoFilter?.value) return;
            const filterValue = timeAgoFilter.value.toString();
            const splitValue = filterValue.split(' ');

            const interval = (
                ['3 months', '6 months', '1 year', '2 years'].includes(filterValue)
                    ? filterValue
                    : 'custom'
            ) as TimeAgoIntervalValue;

            let customAmount = '';
            let customUnit = '';
            if (interval === 'custom') {
                customAmount = splitValue.slice(0, 1).join();
                customUnit = splitValue.length === 1 ? filterValue : splitValue[1];
            }

            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type,
                selectable,
                data: {
                    type,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: {
                            campaigns: transformCampaignFilterToSelectItems(campaignFilter),
                            interval,
                            customAmount,
                            customUnit: customUnit as TimeAgoCustomUnit,
                        },
                    },
                },
            };
        }

        case WorkflowTriggerTypeEnum.PLAN_ANNIVERSARY: {
            const frequencyFilter = step.filters.find(s => s.type === 'frequency');
            const recurringAmountFilter = step.filters.filter(
                s => s.type === 'value' && s.subtype === 'recurring_amount',
            );
            const anniversaryFilter = step.filters.find(s => s.type === 'year');

            const [recurringOperator, recurringAmount, recurringAmountEnd] =
                transformRangeToParts(recurringAmountFilter);

            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type,
                selectable,
                data: {
                    type,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: {
                            frequency: frequencyFilter?.value
                                ? (frequencyFilter.value as WorkflowFilterFrequency)
                                : 'any',
                            anniversary: anniversaryFilter?.value
                                ? (anniversaryFilter?.value?.toString() as AnniversaryYearOptions)
                                : 'any',
                            recurringOperator,
                            recurringAmount,
                            recurringAmountEnd,
                        },
                    },
                },
            };
        }

        case WorkflowTriggerTypeEnum.CONTACT_BIRTHDAY:
        case WorkflowTriggerTypeEnum.CONTACT_TAGGED: {
            const tagFilter = step.filters.find(s => s.type === 'tag');
            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type,
                selectable,
                data: {
                    type,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: {
                            operator: tagFilter?.value as TagsOperator,
                            tags: tagFilter?.values as string[],
                        },
                    },
                },
            };
        }

        case WorkflowTriggerTypeEnum.TEAM_MEMBER_MILESTONE: {
            const campaignFilter = step.filters.find(s => s.type === 'campaign');
            const amountRaisedFilters = step.filters.filter(
                s => s.type === 'value' && s.subtype === 'amount_raised',
            );
            const supportersFilters = step.filters.filter(
                s => s.type === 'value' && s.subtype === 'supporters',
            );

            const [amountRaisedOperator, amountRaised, amountRaisedEnd] =
                transformRangeToParts(amountRaisedFilters);

            const [totalSupportersOperator, totalSupporters, totalSupportersEnd] =
                transformRangeToParts(supportersFilters);

            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type,
                selectable,
                data: {
                    type,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: {
                            campaigns: transformCampaignFilterToSelectItems(campaignFilter),
                            amountRaised,
                            amountRaisedEnd,
                            amountRaisedOperator,
                            totalSupporters,
                            totalSupportersEnd,
                            totalSupportersOperator,
                        },
                    },
                },
            };
        }

        case WorkflowTriggerTypeEnum.CONTACT_TOTAL_CONTRIBUTIONS: {
            const campaignFilter = step.filters.find(s => s.type === 'campaign');
            const contributionFilters = step.filters.filter(
                s => s.type === 'value' && s.subtype === 'total_contributions',
            );

            const [contributionOperator, contributionAmount] =
                transformRangeToParts(contributionFilters);

            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type,
                selectable,
                data: {
                    type,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: {
                            campaigns: transformCampaignFilterToSelectItems(campaignFilter),
                            contributionAmount,
                            contributionOperator,
                        },
                    },
                },
            };
        }
        case WorkflowTriggerTypeEnum.WIDGET_SUBSCRIBED: {
            const widgetFilter = step.filters.find(s => s.type === 'widget');
            return {
                ...baseNodeDefaults,
                id: step.uuid,
                type,
                selectable,
                data: {
                    type,
                    parentId: step.parent_uuid,
                    stepId: step.id,
                    formData: {
                        ...formDefaults,
                        values: {
                            widgets: transformWidgetFilterToSelectItems(widgetFilter),
                        },
                    },
                },
            };
        }
    }
};

const transformOperatorForRequest = (operator: string): WorkflowFilterComparisonOperator => {
    switch (operator) {
        case 'gt':
            return '>';
        case 'lt':
            return '<';
        case 'eq':
        default:
            return '=';
    }
};

const transformOperatorForNode = (operator?: WorkflowFilterComparisonOperator) => {
    if (!operator) return;
    switch (operator) {
        case '>':
            return 'gt';
        case '<':
            return 'lt';
        case '=':
        default:
            return 'eq';
    }
};

const getAmountRangeFilters = (
    subtype: WorkflowFilter['subtype'],
    operator: WorkflowFilterFormComparisonOperator = 'gt',
    amount: string = '',
    amountEnd: string = '',
) => {
    const filters: WorkflowFilter[] = [];

    if (amount) {
        if (operator !== 'bet') {
            filters.push({
                type: 'value',
                subtype: subtype,
                value_type: 'dynamic_value',
                comparison_operator: transformOperatorForRequest(operator),
                value: amount,
            });
        } else {
            filters.push({
                type: 'value',
                subtype: subtype,
                value_type: 'dynamic_value',
                value: amount,
                comparison_operator: '>',
                logical_operator: 'and',
            });
            filters.push({
                type: 'value',
                subtype: subtype,
                value_type: 'dynamic_value',
                value: amountEnd,
                comparison_operator: '<',
            });
        }
    }

    return filters;
};

const getCampaignFilter = (campaigns?: string[]): WorkflowFilter | undefined => {
    if (!campaigns) return undefined;

    if (campaigns.some(c => c === 'any')) {
        return {
            type: 'campaign',
            value_type: 'multiple_values',
            value: 'any',
            comparison_operator: '=',
        };
    }

    return {
        type: 'campaign',
        value_type: 'multiple_values',
        values: campaigns,
        comparison_operator: '=',
    };
};

const getMessageFilter = (messages?: string[]): WorkflowFilter | undefined => {
    if (!messages) return undefined;

    if (messages.some(c => c === 'any')) {
        return {
            type: 'message',
            value_type: 'multiple_values',
            value: 'any',
            comparison_operator: '=',
        };
    }

    return {
        type: 'message',
        value_type: 'multiple_values',
        values: messages,
        comparison_operator: '=',
    };
};

const getWidgetFilter = (widgets?: string[]): WorkflowFilter | undefined => {
    if (!widgets) return undefined;

    if (widgets.some(c => c === 'any')) {
        return {
            type: 'widget',
            value_type: 'multiple_values',
            value: 'any',
            comparison_operator: '=',
        };
    }

    return {
        type: 'widget',
        value_type: 'multiple_values',
        values: widgets,
        comparison_operator: '=',
    };
};

const getFrequencyFilter = (frequency?: WorkflowFilterFrequency): WorkflowFilter | undefined => {
    if (!frequency) return undefined;

    return {
        type: 'frequency',
        value_type: frequency === 'any' ? 'multiple_values' : 'specific_value',
        value: frequency,
        comparison_operator: '=',
    };
};

const getAnniversaryYearFilter = (
    anniversary?: AnniversaryYearOptions,
): WorkflowFilter | undefined => {
    if (!anniversary) return undefined;

    return {
        type: 'year',
        value_type: anniversary === 'any' ? 'multiple_values' : 'specific_value',
        value: anniversary,
        comparison_operator: '=',
    };
};

interface GetTimeAgoFilterOptions {
    interval: TimeAgoIntervalValue;
    customAmount: string;
    customUnit: TimeAgoCustomUnit;
}
const getTimeAgoFilter = (options?: GetTimeAgoFilterOptions): WorkflowFilter | undefined => {
    if (!options) return;
    const { interval, customAmount, customUnit } = options;

    return {
        type: 'time_ago',
        value_type: 'specific_value',
        value: interval === 'custom' ? `${customAmount} ${customUnit}` : interval,
        comparison_operator: '=',
    };
};

const getTagsFilter = (options?: {
    operator: string;
    tags: string[];
}): WorkflowFilter | undefined => {
    if (!options) return;
    const { operator, tags } = options;

    return {
        type: 'tag',
        value_type: 'multiple_values',
        value: operator,
        values: tags,
        comparison_operator: '=',
    };
};

const getIncludeAllTimeFilter = (includeAllTime?: boolean): WorkflowFilter | undefined => {
    if (typeof includeAllTime === 'undefined') return undefined;

    return {
        type: 'include_all_time',
        value: includeAllTime ?? false,
        value_type: 'specific_value',
        comparison_operator: '=',
    };
};

const transformRangeToParts = (
    filters: WorkflowFilter[],
): [WorkflowFilterFormComparisonOperator, string, string] => {
    let operator: WorkflowFilterFormComparisonOperator;
    let amountStart: string | undefined;
    let amountEnd: string | undefined;

    if (filters.length === 0) {
        amountStart = '';
        operator = 'gt';
    } else if (filters.length === 1) {
        amountStart = filters[0].value?.toString();
        operator = transformOperatorForNode(filters[0].comparison_operator) ?? 'gt';
    } else {
        const start = filters.find(s => s.comparison_operator === '>');
        const end = filters.find(s => s.comparison_operator === '<');

        amountStart = start?.value?.toString() ?? '';
        amountEnd = end?.value?.toString() ?? '';
        operator = 'bet';
    }

    return [operator, amountStart ?? '', amountEnd ?? ''];
};

const transformCampaignFilterToSelectItems = (campaignFilter?: WorkflowFilter) => {
    if (!campaignFilter) return [];
    return campaignFilter?.value === 'any' || campaignFilter?.value === 'any_campaign'
        ? [anyCampaignItem.id]
        : campaignFilter?.values?.map(c => c.toString()) || [];
};

const transformMessageFilterToSelectItems = (messageFilter?: WorkflowFilter) => {
    if (!messageFilter) return [];
    return messageFilter?.values?.map(c => c.toString()) || [];
};

const transformWidgetFilterToSelectItems = (widgetFilter?: WorkflowFilter) => {
    if (!widgetFilter) return [];
    return widgetFilter?.value === 'any'
        ? [anyWidgetItem.id]
        : (widgetFilter?.values?.map(c => c.toString()) ?? []);
};

const parseIntOrUndefined = (value: string | undefined) => {
    return value ? parseInt(value) : undefined;
};

const getDelayNodeActionData = (
    formData: FormData<DelayFormValues>,
): DelayWorkflowStep['action_data'] => {
    if (formData.values.preset === 'custom') {
        return {
            value: `${formData.values.custom_value || 0} ${formData.values.custom_unit}`,
            time: formData.values.custom_time?.toISOTime(),
            skip_weekends: formData.values.skip_weekends,
        };
    }
    return {
        value: formData.values.preset,
        skip_weekends: formData.values.skip_weekends,
    };
};

const getBranchFiltersFromValue = (values: BranchFormValues) => {
    const { value, include_all_time } = values;
    let filters: WorkflowFilter[] = [];

    if (value) {
        if ('campaigns' in value) {
            filters = filterUndefined([
                ...filters,
                getCampaignFilter(value.campaigns),
                getIncludeAllTimeFilter(include_all_time),
            ]);
        }
        if ('tags' in value) {
            filters = filterUndefined([...filters, getTagsFilter(value)]);
        }
        if ('messages' in value) {
            filters = filterUndefined([...filters, getMessageFilter(value.messages)]);
        }
    }

    return filters;
};

const formDefaults = {
    isDirty: false,
    isValid: false,
    errors: {},
    values: {},
};

export { transformNodesToSteps, transformStepsToNodes, formDefaults };
